import React, { useState, useEffect } from "react";
import { db } from "../utils/firebase";
import {
  collection,
  addDoc,
  orderBy,
  limit,
  updateDoc,
  doc,
  query,
  getDocs,
} from "firebase/firestore";
import { serverTimestamp } from "firebase/firestore";
import { useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Spinner } from "./index.js";
import { useAuth } from "../hooks/AuthenticationProvider";
import { BarChart3, Users, Activity, Heart, User } from "lucide-react";

const RecoveryCapital = () => {
  const { state: locationState } = useLocation();
  const {
    organizationId,
    user,
    organizationData: { residents: orgResidents } = {},
    checkPermission,
    isLoading,
  } = useAuth();

  const [hasKids, setHasKids] = useState(false);
  const [hasBeenInPrison, setHasBeenInPrison] = useState(false);
  const navigate = useNavigate();
  const [openTooltips, setOpenTooltips] = useState({});
  const [filteredResidents, setFilteredResidents] = useState([]);
  const [socialCapitalScore, setSocialCapitalScore] = useState(0);
  const [physicalCapitalScore, setPhysicalCapitalScore] = useState(0);
  const [individualCapitalScore, setIndividualCapitalScore] = useState(0);
  const [spiritualCapitalScore, setSpiritualCapitalScore] = useState(0);
  const [generalCapitalScore, setGeneralCapitalScore] = useState(0);
  const initialFormData = {
    residentName: locationState?.residentName || "",
    questionnaire: locationState?.questionnaire || "",
    overOneYearInPrison: locationState?.overOneYearInPrison || "",
    adverseExperiences: "",
    sponsor: "",
    employed: "",
    therapy: "",
    criminalJustice: "",
    jailExperienceEffect: "",
    kids: "",
    totalRecoveryTime: "",
    reliableTransportation: "",
    educationLevel: "",
    lastStepCompleted: "",
    lifeSatisfaction: "",
    cravings: "",
    supportOthers: "",
    strongRelationKids: "",
    strongRelationFamily: "",
    friendsInRecovery: "",
    authenticSelf: "",
    feelOutcast: "",
    healthySocialGroup: "",
    rebuiltRelationships: "",
    familyDysfunction: "",
    partOfCommunity: "",
    bodyImage: "",
    enoughMoney: "",
    pain: "",
    motivation: "",
    medicalInsurance: "",
    homelessOneYear: "",
    childhoodTrauma: "",
    twoParentsHome: "",
    generalHygeine: "",
    workoutRegularly: "",
    eatHealthy: "",
    sleep: "",
    coOccurringDiagnosis: "",
    physicallyHealthy: "",
    healthyBoundaries: "",
    hardToHaveFun: "",
    guiltOrShame: "",
    decisionMakingAbilities: "",
    selfImprovement: "",
    mentalHealthProblems: "",
    lifeHasPurpose: "",
    hardToTrust: "",
    emotionalRegulation: "",
    happyFuture: "",
    hardToLetGo: "",
    spirituallyConnected: "",
    spiritualGrowth: "",
    innerPeace: "",
    forgiveness: "",
    spiritualPractices: "",
    greaterPurpose: "",
    deepGratitude: "",
    selfReflection: "",
    moralConviction: "",
    staffNotes: "",
  };
  const [formState, setFormState] = useState({
    // Renamed from state to formState
    formData: initialFormData,
  });
  const stickyFields = [
    "kids",
    "overOneYearInPrison",
    "homelessOneYear",
    "childhoodTrauma",
    "twoParentsHome",
    "coOccurringDiagnosis",
  ];

  useEffect(() => {
    const handleClickOutside = () => {
      setOpenTooltips({});
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!orgResidents) return;
    const currentResidents = orgResidents
      .filter((resident) => resident.currentResident)
      .map((resident) => ({
        id: resident.id,
        fullName: resident.fullName,
      }))
      .sort((a, b) =>
        a.fullName.localeCompare(b.fullName, undefined, { sensitivity: "base" })
      );
    setFilteredResidents(currentResidents);
  }, [orgResidents]);

  useEffect(() => {
    // run when a resident is picked
    const residentName = formState.formData.residentName;
    if (!residentName || !organizationId) return;

    // find the resident ID (needed for their sub‑collection path)
    const match = filteredResidents.find((r) => r.fullName === residentName);
    if (!match) return;

    (async () => {
      try {
        // newest doc first, only 1
        const rcRef = collection(
          db,
          `organizations/${organizationId}/residents/${match.id}/recoveryCapital`
        );
        const q = query(rcRef, orderBy("timestamp", "desc"), limit(1));
        const snap = await getDocs(q);
        if (snap.empty) return; // resident has no prior surveys

        const latest = snap.docs[0].data();

        // clone current form data
        const next = { ...formState.formData };

        // copy only the sticky fields **if they’re still blank**
        stickyFields.forEach((field) => {
          if (!next[field] && latest[field] != null) {
            next[field] = latest[field];
          }
        });

        // update yes/no UI helpers
        if (latest.kids) setHasKids(latest.kids === "Yes");
        if (latest.overOneYearInPrison)
          setHasBeenInPrison(latest.overOneYearInPrison === "Yes");

        setFormState({ formData: next });
      } catch (err) {
        console.error("Failed to pre‑fill follow‑up survey:", err);
      }
    })();
  }, [formState.formData.residentName, filteredResidents, organizationId]);

  useEffect(() => {
    if (locationState) {
      setHasBeenInPrison(locationState.overOneYearInPrison === "Yes");
      setHasKids(locationState.kids === "Yes");
    }
  }, [locationState]);

  // Permissions check effect
  useEffect(() => {
    if (!isLoading && !checkPermission("viewRecoveryCapital")) {
      navigate("/");
    }
  }, [isLoading, checkPermission, navigate]);

  // Update handleChange to use formState instead of state
  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedFormData = { ...formState.formData, [name]: value };
    setFormState({ formData: updatedFormData });
    recalculateScores(updatedFormData);
  };

  const handleHasKidsChange = (e) => {
    const value = e.target.value;
    setHasKids(value === "Yes");
    setFormState((prevState) => ({
      ...prevState,
      formData: {
        ...prevState.formData,
        kids: value,
      },
    }));
  };

  // Then update the generateSelectOptions function
  const generateSelectOptions = (
    sectionName,
    question,
    formData,
    handleChange
  ) => {
    return (
      <div key={question.id} className="flex flex-col">
        <label
          className="text-sm md:text-sm font-bold flex items-center"
          htmlFor={question.id}
        >
          {question.text}
          {question.tooltip && (
            <div className="relative ml-2 inline-block">
              <div
                className="text-cyan-400 cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setOpenTooltips((prev) => ({
                    ...prev,
                    [question.id]: !prev[question.id],
                  }));
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-5 h-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                  />
                </svg>
              </div>
              {openTooltips[question.id] && (
                <div className="absolute z-50 w-64 px-4 py-2 text-sm text-white bg-slate-800 rounded-lg shadow-lg right-0 bottom-full mb-2">
                  {question.tooltip}
                  <div className="absolute w-3 h-3 bg-slate-800 transform rotate-45 right-6 -bottom-1"></div>
                </div>
              )}
            </div>
          )}
        </label>
        <select
          className="peer w-full py-2 px-1 text-sm bg-transparent border-0 border-b-2 border-white leading-tight focus:border-blue-500 focus:ring-0 placeholder-transparent"
          name={question.id}
          value={formData[question.id]}
          onChange={(e) => {
            if (question.id === "overOneYearInPrison") {
              handleBeenInPrisonChange(e);
            } else {
              handleChange(e);
            }
          }}
        >
          <option value="">Select an option</option>
          {question.responses ? (
            question.responses.map((response, index) => (
              <option key={index} value={index + 1}>
                {response}
              </option>
            ))
          ) : (
            <>
              <option value="1">1 - Strongly Disagree</option>
              <option value="2">2 - Disagree</option>
              <option value="3">3 - Agree</option>
              <option value="4">4 - Strongly Agree</option>
            </>
          )}
        </select>
      </div>
    );
  };

  // New function to handle score recalculations
  function recalculateScores(updatedFormData) {
    setSocialCapitalScore(
      calculateScore(updatedFormData, socialCapitalQuestions, "socialCapital")
    );
    setPhysicalCapitalScore(
      calculateScore(
        updatedFormData,
        physicalCapitalQuestions,
        "physicalCapital"
      )
    );
    setIndividualCapitalScore(
      calculateScore(
        updatedFormData,
        individualCapitalQuestions,
        "individualCapital"
      )
    );
    setSpiritualCapitalScore(
      calculateScore(
        updatedFormData,
        spiritualCapitalQuestions,
        "spiritualCapital"
      )
    );
    setGeneralCapitalScore(calculateGeneralQuestionScore(updatedFormData));
  }

  const generalCapitalQuestions = [
    {
      text: "I speak to my sponsor:",
      id: "sponsor",
      responses: [
        "Not at all",
        "Once a week",
        "2-3 times a week",
        "4+ times a week",
      ],
    },
    {
      text: "I have consistent access to reliable transportation for my recovery activities",
      id: "reliableTransportation",
    },
    {
      text: "I am currently employed.",
      id: "employed",
      responses: [
        "Currently unemployed and not seeking employment",
        "Currently unemployed but actively seeking employment",
        "Currently employed part-time (this includes regular community service)",
        "Currently employed full-time",
      ],
    },
    {
      text: "I attend therapy sessions with a licensed professional.",
      id: "therapy",
      responses: [
        "I do not attend therapy sessions",
        "I sporadically attend therapy sessions",
        "I attend therapy sessions every other week",
        "I consistently attend therapy sessions every week",
      ],
    },
    {
      text: "The criminal justice system (e.g., probation/parole, pending cases) impacts my recovery negatively.",
      id: "criminalJustice",
    },
    { text: "Do you have kids?", id: "kids" },
  ];

  const socialCapitalQuestions = [
    {
      text: "I actively help and support others in their recovery journey",
      id: "supportOthers",
    },
    {
      text: "I have friends in recovery who I can turn to for advice and support",
      id: "friendsInRecovery",
    },
    {
      text: "I can be authentically myself around the people I socialize with. There are no parts that I need to hide.",
      id: "authenticSelf",
    },
    { text: "I feel like an outcast", id: "feelOutcast" },
    {
      text: "I am never around people who use drugs and/or alcohol",
      id: "healthySocialGroup",
      tooltip:
        "Consider your entire environment, including work. For example, if you work as a server where alcohol is served, you would answer 'Strongly Disagree' to this question.",
    },
    {
      text: "I have rebuilt important relationships that were damaged due to addiction",
      id: "rebuiltRelationships",
    },
    {
      text: "My family engages in behaviors that hinder my recovery",
      id: "familyDysfunction",
    },
    {
      text: "I am comfortable setting and maintaining healthy boundaries in my relationships",
      id: "healthyBoundaries",
    },
    {
      text: "I feel like I'm part of a recovery community",
      id: "partOfCommunity",
    },
  ];

  const physicalCapitalQuestions = [
    { text: "I feel comfortable with my body image.", id: "bodyImage" },
    {
      text: "I have enough money every week to buy the basic things I need",
      id: "enoughMoney",
    },
    { text: "I am regularly in  physical pain", id: "pain" },
    { text: "I feel motivated most days", id: "motivation" },
    {
      text: "I have medical insurance that covers my medical needs",
      id: "medicalInsurance",
    },
    { text: "My general hygeine is well taken care of", id: "generalHygeine" },
    { text: "I work out 3-5 times a week", id: "workoutRegularly" },
    { text: "I eat generally healthy", id: "eatHealthy" },
    { text: "I sleep for 7-9 hours a night", id: "sleep" },
    { text: "I feel physically healthy most days", id: "physicallyHealthy" },
  ];

  const individualCapitalQuestions = [
    { text: "I find it hard to have fun", id: "hardToHaveFun" },
    { text: "I struggle with guilt or shame", id: "guiltOrShame" },
    {
      text: "I am confident in my decision-making abilities",
      id: "decisionMakingAbilities",
    },
    {
      text: "I actively seek out resources or knowledge to improve myself.",
      id: "selfImprovement",
    },
    {
      text: "I currently have problems with my mental health",
      id: "mentalHealthProblems",
    },
    { text: "I feel my life has purpose and meaning", id: "lifeHasPurpose" },
    { text: "It’s hard for me to trust others", id: "hardToTrust" },
    {
      text: "I consistently experience cravings to use drugs/alcohol.",
      id: "cravings",
    },
    {
      text: "I am good at regulating myself emotionally",
      id: "emotionalRegulation",
    },
    {
      text: "It's hard to let go of the part of my identity that was linked to my drinking or drug use",
      id: "hardToLetGo",
      tooltip:
        "This question addresses the challenge of separating your recovery identity from your addiction identity. In early recovery, it can be difficult to see yourself as separate from your past behaviors. This is about your ability to form a new self-concept that isn't defined by addiction.",
    },
  ];

  const spiritualCapitalQuestions = [
    { text: "I feel spiritually connected", id: "spirituallyConnected" },
    {
      text: "My spiritual growth is important to my overall well-being",
      id: "spiritualGrowth",
    },
    { text: "I feel a deep sense of inner peace", id: "innerPeace" },
    {
      text: "I believe forgiveness is necessary in my healing journey",
      id: "forgiveness",
    },
    {
      text: "I regularly engage in spiritual practices (e.g., prayer, meditation)",
      id: "spiritualPractices",
    },
    {
      text: "I believe my recovery has a greater purpose beyond myself",
      id: "greaterPurpose",
    },
    {
      text: "I experience moments of deep gratitude in my journey",
      id: "deepGratitude",
    },
    {
      text: "I take time for self-reflection regularly.",
      id: "selfReflection",
    },
    {
      text: "I regularly follow and adhere to my moral and ethical compass",
      id: "moralConviction",
    },
    {
      text: "I believe I will have a bright, happy, and healthy future",
      id: "happyFuture",
    },
  ];

  // Conditionally add the question based on 'hasKids' state
  if (hasKids === true) {
    socialCapitalQuestions.splice(1, 0, {
      text: "I have strong relationships with my kids",
      id: "strongRelationKids",
    });
  } else if (hasKids === false) {
    socialCapitalQuestions.splice(1, 0, {
      text: "I have strong relationships with my immediate family",
      id: "strongRelationFamily",
    });
  }

  const kidsQuestionIndex = generalCapitalQuestions.findIndex(
    (q) => q.id === "kids"
  );
  let kidsQuestion;
  if (kidsQuestionIndex !== -1) {
    [kidsQuestion] = generalCapitalQuestions.splice(kidsQuestionIndex, 1);
  }

  const handleBeenInPrisonChange = (e) => {
    const value = e.target.value;
    setHasBeenInPrison(value === "Yes");
    setFormState((prevState) => ({
      ...prevState,
      formData: {
        ...prevState.formData,
        overOneYearInPrison: value,
      },
    }));
  };

  if (hasBeenInPrison === true) {
    generalCapitalQuestions.splice(3, 0, {
      text: "Does your time in jail/prison negatively affect your life today?",
      id: "jailExperienceEffect",
      responses: [
        "It's a constant weight holding me back.",
        "It's a part of my past I'm trying to move beyond.",
        "I've learned lessons from it that sometimes help in my life and recovery.",
        "It was a turning point that has positively influenced my recovery journey.",
      ],
    });
  } else if (hasBeenInPrison === false) {
    generalCapitalQuestions.splice(3, 0, {
      text: "There are adverse experiences from my past that continue to negatively impact my life today.",
      id: "adverseExperiences",
      responses: ["Strongly Agree", "Agree", "Disagree", "Strongly Disagree"],
    });
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!checkPermission("addRecoveryCapital")) return;

    // Find the resident object that corresponds to the selected resident name FIRST
    const selectedResident = filteredResidents.find(
      (resident) => resident.fullName === formState.formData.residentName
    );

    if (!selectedResident) {
      console.error(
        "No matching resident found for:",
        formState.formData.residentName
      );
      return;
    }

    const recoveryCapitalTotal =
      socialCapitalScore +
      physicalCapitalScore +
      individualCapitalScore +
      spiritualCapitalScore +
      generalCapitalScore;

    // Prepare the data document to be written in both collections
    const recoveryCapitalData = {
      ...formState.formData,
      recoveryCapitalTotal,
      generalCapitalScore,
      socialCapitalScore,
      physicalCapitalScore,
      individualCapitalScore,
      spiritualCapitalScore,
      residentId: selectedResident.id, // Now selectedResident is already defined
      timestamp: serverTimestamp(),
    };

    // Prepare risk factor data to update in the resident document
    const riskFactorData = {
      overOneYearInPrison: formState.formData.overOneYearInPrison || "",
      homelessOneYear: formState.formData.homelessOneYear || "",
      childhoodTrauma: formState.formData.childhoodTrauma || "",
      twoParentsHome: formState.formData.twoParentsHome || "",
      coOccurringDiagnosis: formState.formData.coOccurringDiagnosis || "",
    };

    // Only include fields that have values (not empty strings)
    const filteredRiskFactorData = Object.fromEntries(
      Object.entries(riskFactorData).filter(([key, value]) => value !== "")
    );

    try {
      // Create the resident document reference
      const residentDocRef = doc(
        db,
        `organizations/${organizationId}/residents/${selectedResident.id}`
      );

      // Write to all three locations simultaneously using Promise.all:
      await Promise.all([
        // 1. Add to organization-level recovery capital collection
        addDoc(
          collection(db, `organizations/${organizationId}/recoveryCapital`),
          recoveryCapitalData
        ),
        // 2. Add to resident-specific recovery capital subcollection
        addDoc(
          collection(
            db,
            `organizations/${organizationId}/residents/${selectedResident.id}/recoveryCapital`
          ),
          recoveryCapitalData
        ),
        // 3. Update the resident document with risk factor data (only if there are fields to update)
        ...(Object.keys(filteredRiskFactorData).length > 0
          ? [updateDoc(residentDocRef, filteredRiskFactorData)]
          : []),
      ]);

      console.log(
        "Recovery capital survey submitted and resident document updated successfully"
      );
      navigate("/recoverycapitaldata");
    } catch (error) {
      console.error("Error submitting recovery capital:", error);
    }
  };

  // List of question IDs that have reversed scoring
  const reversedScoringQuestions = [
    "feelOutcast",
    "familyDysfunction",
    "pain",
    "hardToHaveFun",
    "guiltOrShame",
    "mentalHealthProblems",
    "hardToTrust",
    "cravings", // ← Fixed: was "craving"
    "hardToLetGo",
    "criminalJustice",
  ];

  const calculateScore = (formData, questions, prefix) => {
    let score = 0;

    questions.forEach((question) => {
      const value = formData[question.id];

      // If value exists, then proceed to calculation
      if (value) {
        const parsedValue = parseInt(value, 10);
        // Check if the current question needs reversed scoring
        if (reversedScoringQuestions.includes(question.id)) {
          score += 5 - parsedValue; // 5 - value will reverse 1-4 to 4-1
        } else {
          score += parsedValue;
        }
      }
    });

    return score;
  };

  const calculateGeneralQuestionScore = (formData) => {
    let score = 0; // Initialize score to 0

    console.log("formData:", formData);

    // Loop for all general questions
    generalCapitalQuestions.forEach((question) => {
      const value = parseInt(formData[question.id], 10);
      if (!isNaN(value)) {
        if (reversedScoringQuestions.includes(question.id)) {
          score += 5 - value; // 5 - value will reverse 1-4 to 4-1
        } else {
          score += value;
        }
      }
    });

    // Placeholder logic for notBeenInPrisonPlaceholder (this can be expanded later based on your requirements)
    const notBeenInPrisonPlaceholder = formData["notBeenInPrisonPlaceholder"];
    if (notBeenInPrisonPlaceholder) {
      score += parseInt(notBeenInPrisonPlaceholder, 10);
    }

    const totalRecoveryTime = parseInt(formData["totalRecoveryTime"], 10);
    if (!isNaN(totalRecoveryTime)) {
      score += totalRecoveryTime;
    } else {
      console.log(
        "Error parsing totalRecoveryTime:",
        formData["totalRecoveryTime"]
      );
    }

    const educationLevel = parseInt(formData["educationLevel"], 10);
    if (!isNaN(educationLevel)) {
      score += educationLevel;
    } else {
      console.log("Error parsing educationLevel:", formData["educationLevel"]);
    }

    // Add logic for "Last step that you have currently completed"
    const lastStepValue = formData["lastStepCompleted"];
    const lastStep = parseInt(lastStepValue, 10);

    if (lastStepValue === "None") {
      score += 1;
    } else if (lastStep >= 1 && lastStep <= 5) {
      score += 2;
    } else if (lastStep >= 6 && lastStep <= 9) {
      score += 3;
    } else if (lastStep >= 10 && lastStep <= 12) {
      score += 4;
    } else {
      console.warn("Unexpected value for lastStepCompleted:", lastStepValue);
    }

    // Add logic for "Overall Satisfaction with life"
    const satisfaction = parseInt(formData["lifeSatisfaction"], 10);
    if (satisfaction >= 1 && satisfaction <= 2) {
      score += 1;
    } else if (satisfaction >= 3 && satisfaction <= 5) {
      score += 2;
    } else if (satisfaction >= 6 && satisfaction <= 8) {
      score += 3;
    } else if (satisfaction >= 9 && satisfaction <= 10) {
      score += 4;
    }

    return score;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[80vh]">
        <Spinner />
      </div>
    );
  }

  if (!user) {
    navigate("/login");
    return null;
  }

  return (
    <>
      <Helmet>
        <title>&bull; Recovery Capital</title>
      </Helmet>

      <div className="min-h-screen bg-[#18181B] bg-gradient-to-br from-zinc-900 via-zinc-900/95 to-zinc-950 p-6 relative overflow-hidden">
        {/* Ambient background effects */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(24,24,27,0.3),rgba(0,0,0,0))]" />
        <div className="absolute inset-0">
          <div className="absolute top-0 -left-4 w-96 h-96 bg-cyan-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse delay-700" />
        </div>

        <div className="max-w-7xl mx-auto space-y-8 relative">
          {/* Header */}
          <div className="flex items-center gap-6 mb-12">
            <div className="flex gap-3">
              <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse" />
              <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-100 opacity-70" />
              <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-200 opacity-40" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-white tracking-tight">
                Recovery Capital Survey
              </h1>
            </div>
          </div>

          {/* Form Container */}
          <form onSubmit={handleSubmit} className="space-y-8 relative z-10">
            {/* Initial Selection Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
              {/* Select Resident */}
              <div className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl p-4 border border-slate-800/50">
                <label className="text-white font-medium mb-2 block">
                  Select Resident
                </label>
                <select
                  name="residentName"
                  value={formState.formData["residentName"]}
                  onChange={handleChange}
                  className="w-full bg-slate-800 text-white rounded-lg px-4 py-2 border border-slate-700"
                >
                  <option value="">Select an Option</option>
                  {filteredResidents.map((resident, index) => (
                    <option key={index} value={resident.fullName}>
                      {resident.fullName}
                    </option>
                  ))}
                </select>
              </div>

              {/* What Questionnaire is this? */}
              <div className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl p-4 border border-slate-800/50">
                <label className="text-white font-medium mb-2 block">
                  What Questionnaire is this?
                </label>
                <select
                  name="questionnaire"
                  onChange={handleChange}
                  value={formState.formData["questionnaire"] || "Select"}
                  className="w-full bg-slate-800 text-white rounded-lg px-4 py-2 border border-slate-700"
                >
                  <option value="Select">Select an Option</option>
                  <option value="Intake">Intake</option>
                  <option value="1 Month">1 Month</option>
                  <option value="2 Months">2 Months</option>
                  <option value="3 Months">3 Months</option>
                  <option value="6 Months">6 Months</option>
                  <option value="9 Months">9 Months</option>
                  <option value="1 Year">1 Year</option>
                  <option value="18 Months">18 Months</option>
                  <option value="2 Years">2 Years</option>
                </select>
              </div>

              {/* Do you have kids? */}
              <div className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl p-4 border border-slate-800/50">
                <label className="text-white font-medium mb-2 block">
                  Do you have kids?
                </label>
                {kidsQuestion && (
                  <select
                    className="w-full bg-slate-800 text-white rounded-lg px-4 py-2 border border-slate-700"
                    name={kidsQuestion.id}
                    value={formState.formData[kidsQuestion.id]}
                    onChange={handleHasKidsChange}
                  >
                    <option value="">Select an Option</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                )}
              </div>

              {/* Over 1 year in jails/prisons? */}
              <div className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl p-4 border border-slate-800/50">
                <label className="text-white font-medium mb-2 block">
                  Have you spent over 1 year in jails/prisons throughout your
                  life?
                </label>
                <select
                  className="w-full bg-slate-800 text-white rounded-lg px-4 py-2 border border-slate-700"
                  name="overOneYearInPrison"
                  value={formState.formData["overOneYearInPrison"] || ""}
                  onChange={handleBeenInPrisonChange}
                >
                  <option value="">Select an Option</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>

              {/* Homeless for over 1 year in your life? */}
              <div className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl p-4 border border-slate-800/50">
                <label className="text-white font-medium mb-2 block">
                  Have you been homeless for over one year throughout your life?
                </label>
                <select
                  className="w-full bg-slate-800 text-white rounded-lg px-4 py-2 border border-slate-700"
                  name="homelessOneYear"
                  value={formState.formData["homelessOneYear"] || ""}
                  onChange={handleChange}
                >
                  <option value="">Select an Option</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>

              {/* Childhood trauma / abuse / neglect? */}
              <div className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl p-4 border border-slate-800/50">
                <label className="text-white font-medium mb-2 block">
                  Have you experienced childhood trauma or any form of abuse or
                  neglect?
                </label>
                <select
                  className="w-full bg-slate-800 text-white rounded-lg px-4 py-2 border border-slate-700"
                  name="childhoodTrauma"
                  value={formState.formData["childhoodTrauma"] || ""}
                  onChange={handleChange}
                >
                  <option value="">Select an Option</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>

              {/* Two parents in same home growing up? */}
              <div className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl p-4 border border-slate-800/50">
                <label className="text-white font-medium mb-2 block flex items-center">
                  Did you have a two-parent household growing up?
                  <div className="relative ml-2 inline-block">
                    <div className="text-cyan-400 cursor-help">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-5 h-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                        />
                      </svg>
                    </div>
                    <div className="absolute z-50 w-64 px-4 py-2 text-sm text-white bg-slate-800 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-300 right-0 bottom-full mb-2">
                      Note: Stepparents count toward this. Answer "Yes" only if
                      two parents were present continuously throughout your
                      childhood. If parents separated/divorced while you were
                      growing up, select "No".
                      <div className="absolute w-3 h-3 bg-slate-800 transform rotate-45 right-6 -bottom-1"></div>
                    </div>
                  </div>
                </label>
                <select
                  className="w-full bg-slate-800 text-white rounded-lg px-4 py-2 border border-slate-700"
                  name="twoParentsHome"
                  value={formState.formData["twoParentsHome"] || ""}
                  onChange={handleChange}
                >
                  <option value="">Select an Option</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>

              {/* Formal mental‑health diagnosis? */}
              <div className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl p-4 border border-slate-800/50">
                <label className="text-white font-medium mb-2 block">
                  Have you ever been diagnosed with a mental‑health disorder?
                </label>
                <select
                  className="w-full bg-slate-800 text-white rounded-lg px-4 py-2 border border-slate-700"
                  name="coOccurringDiagnosis"
                  value={formState.formData.coOccurringDiagnosis || ""}
                  onChange={handleChange}
                >
                  <option value="">Select an Option</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
            </div>

            <div className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-hidden p-6 border border-slate-800/50">
              <h2 className="text-xl font-bold text-white mb-6 flex items-center justify-center gap-3">
                <div className="p-2 bg-cyan-500/10 rounded-lg">
                  <BarChart3 className="text-cyan-400" />
                </div>
                General Questions
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Last Step Completed */}
                <div className="relative bg-slate-800/50 rounded-lg p-4">
                  <label
                    className="text-white font-medium mb-3 block"
                    htmlFor="lastStepCompleted"
                  >
                    What is the last step that you have currently completed?
                  </label>
                  <select
                    className="w-full bg-slate-800 text-white rounded-lg px-4 py-2 border border-slate-700"
                    name="lastStepCompleted"
                    value={formState.formData["lastStepCompleted"]}
                    onChange={handleChange}
                  >
                    <option value="">Select an Option</option>
                    <option value="None">None</option>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(
                      (step, index) => (
                        <option key={index} value={step}>
                          {step}
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* Total Recovery Time */}
                <div className="relative bg-slate-800/50 rounded-lg p-4">
                  <label
                    className="text-white font-medium mb-3 block"
                    htmlFor="totalRecoveryTime"
                  >
                    Total Recovery Time
                  </label>
                  <select
                    className="w-full bg-slate-800 text-white rounded-lg px-4 py-2 border border-slate-700"
                    name="totalRecoveryTime"
                    value={formState.formData["totalRecoveryTime"]}
                    onChange={handleChange}
                  >
                    <option value="">Select an Option</option>
                    <option value="1">0-6 months</option>
                    <option value="2">6 months-1 year</option>
                    <option value="3">1-3 years</option>
                    <option value="4">3+ years</option>
                  </select>
                </div>

                {/* Education Level */}
                <div className="relative bg-slate-800/50 rounded-lg p-4">
                  <label
                    className="text-white font-medium mb-3 block"
                    htmlFor="educationLevel"
                  >
                    Education Level
                  </label>
                  <select
                    className="w-full bg-slate-800 text-white rounded-lg px-4 py-2 border border-slate-700"
                    name="educationLevel"
                    value={formState.formData["educationLevel"]}
                    onChange={handleChange}
                  >
                    <option value="">Select an Option</option>
                    <option value="1">No GED/Diploma</option>
                    <option value="2">GED/Diploma</option>
                    <option value="3">Some College</option>
                    <option value="4">Associates Degree or above</option>
                  </select>
                </div>

                {/* Life Satisfaction */}
                <div className="relative bg-slate-800/50 rounded-lg p-4">
                  <label
                    className="text-white font-medium mb-3 block flex items-center"
                    htmlFor="lifeSatisfaction"
                  >
                    Reflecting on the bigger picture, what is your overall
                    satisfaction with life?
                    <div className="relative ml-2 inline-block">
                      <div
                        className="text-cyan-400 cursor-pointer"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setOpenTooltips((prev) => ({
                            ...prev,
                            lifeSatisfaction: !prev.lifeSatisfaction,
                          }));
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                          />
                        </svg>
                      </div>
                      {openTooltips.lifeSatisfaction && (
                        <div className="absolute z-50 w-64 px-4 py-2 text-sm text-white bg-slate-800 rounded-lg shadow-lg right-0 bottom-full mb-2">
                          Focus on your overall life journey, not temporary
                          situations. Consider your long-term wellbeing rather
                          than recent bad days or acute stressors. Rate your
                          life satisfaction based on the complete picture of
                          your life and recovery.
                          <div className="absolute w-3 h-3 bg-slate-800 transform rotate-45 right-6 -bottom-1"></div>
                        </div>
                      )}
                    </div>
                  </label>
                  <select
                    className="w-full bg-slate-800 text-white rounded-lg px-4 py-2 border border-slate-700"
                    name="lifeSatisfaction"
                    value={formState.formData["lifeSatisfaction"]}
                    onChange={handleChange}
                  >
                    <option value="">Select an Option</option>
                    {Array.from({ length: 10 }, (_, i) => i + 1).map(
                      (rating, index) => (
                        <option key={index} value={rating}>
                          {rating}
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* General Capital Questions */}
                {generalCapitalQuestions.map((questionObject, index) => (
                  <div
                    className="relative bg-slate-800/50 rounded-lg p-4"
                    key={index}
                  >
                    {generateSelectOptions(
                      "general",
                      questionObject,
                      formState.formData,
                      handleChange
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Social Capital Section */}
            <div className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-hidden p-6 border border-slate-800/50">
              <h2 className="text-xl font-bold text-white mb-6 flex items-center justify-center gap-3">
                <div className="p-2 bg-cyan-500/10 rounded-lg">
                  <Users className="text-cyan-400" />
                </div>
                Social Capital
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {socialCapitalQuestions.map((questionObject, index) => (
                  <div
                    key={index}
                    className="relative bg-slate-800/50 rounded-lg p-4"
                  >
                    {generateSelectOptions(
                      "socialCapital",
                      questionObject,
                      formState.formData,
                      handleChange
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Individual Capital Section */}
            <div className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-hidden p-6 border border-slate-800/50">
              <h2 className="text-xl font-bold text-white mb-6 flex items-center justify-center gap-3">
                <div className="p-2 bg-cyan-500/10 rounded-lg">
                  <User className="text-cyan-400" />
                </div>
                Individual Capital
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {individualCapitalQuestions.map((questionObject, index) => (
                  <div
                    key={index}
                    className="relative bg-slate-800/50 rounded-lg p-4"
                  >
                    {generateSelectOptions(
                      "individualCapital",
                      questionObject,
                      formState.formData,
                      handleChange
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Physical Capital Section */}
            <div className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-hidden p-6 border border-slate-800/50">
              <h2 className="text-xl font-bold text-white mb-6 flex items-center justify-center gap-3">
                <div className="p-2 bg-cyan-500/10 rounded-lg">
                  <Activity className="text-cyan-400" />
                </div>
                Physical Capital
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {physicalCapitalQuestions.map((questionObject, index) => (
                  <div
                    key={index}
                    className="relative bg-slate-800/50 rounded-lg p-4"
                  >
                    {generateSelectOptions(
                      "physicalCapital",
                      questionObject,
                      formState.formData,
                      handleChange
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Spiritual Capital Section */}
            <div className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-hidden p-6 border border-slate-800/50">
              <h2 className="text-xl font-bold text-white mb-6 flex items-center justify-center gap-3">
                <div className="p-2 bg-cyan-500/10 rounded-lg">
                  <Heart className="text-cyan-400" />
                </div>
                Spiritual Capital
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {spiritualCapitalQuestions.map((questionObject, index) => (
                  <div
                    key={index}
                    className="relative bg-slate-800/50 rounded-lg p-4"
                  >
                    {generateSelectOptions(
                      "spiritualCapital",
                      questionObject,
                      formState.formData,
                      handleChange
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Scores Display */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 mb-8">
              {[
                { label: "Social Capital Score", value: socialCapitalScore },
                {
                  label: "Physical Capital Score",
                  value: physicalCapitalScore,
                },
                {
                  label: "Individual Capital Score",
                  value: individualCapitalScore,
                },
                {
                  label: "Spiritual Capital Score",
                  value: spiritualCapitalScore,
                },
                { label: "General Capital Score", value: generalCapitalScore },
              ].map((score) => (
                <div
                  key={score.label}
                  className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl p-4 border border-slate-800/50"
                >
                  <div className="text-cyan-400 font-semibold">
                    {score.label}
                  </div>
                  <div className="text-2xl text-white font-bold">
                    {score.value}
                  </div>
                </div>
              ))}
            </div>

            {/* Notes Section */}
            <div className="group relative bg-slate-900/50 backdrop-blur-xl rounded-xl p-6 border border-slate-800/50 mb-6">
              <label
                className="text-white font-medium mb-3 block"
                htmlFor="staffNotes"
              >
                Notes
              </label>
              <textarea
                className="w-full bg-slate-800 text-white rounded-lg px-4 py-2 border border-slate-700 min-h-[100px] focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                rows="4"
                name="staffNotes"
                value={formState.formData.staffNotes}
                onChange={handleChange}
                placeholder="Write any important notes here."
              />
            </div>

            {/* Submit Button */}
            <button className="w-full px-6 py-3 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white rounded-lg transition-all duration-300 shadow-lg hover:shadow-cyan-500/25">
              Submit Survey
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default RecoveryCapital;
